import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { setPageNameHeader } from "src/redux/actions/header";
import { getUserData } from "src/redux/selectors/user";
import { FirebaseDataService } from "../../services/Firebase/data";
import { Content, colors } from "src/styles";
import styled, { css } from "styled-components";

import {
  buildPmReportDownload,
  getPmDashboardData,
  getPreventativeMaintenanceProgram,
  PM_MONTHS,
  normalizePreventativeMaintenanceProgram,
  PmDashboardData,
  PmProgramData,
  PmService,
  PmSite,
  PmVisitRecord,
} from "./data";

type PmViewMode = "status" | "scheduled";

const statusLabelMap: Record<PmVisitRecord["status"], string> = {
  completed: "Completed",
  scheduled: "Scheduled",
  pending: "Pending",
};

const formatPmDateTime = (value?: string) => {
  if (!value) {
    return "-";
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const monthSortValue = (visit: PmVisitRecord) => visit.year * 12 + PM_MONTHS.indexOf(visit.month);

const buildEmptyPmProgram = (clientName = "", portfolioName = ""): PmProgramData => ({
  clientName,
  portfolioName,
  staticData: [],
  services: [],
});

const PreventativeMaintenance = () => {
  const navigate = useNavigate();
  const { service: serviceParam } = useParams<{ service?: string }>();
  const selectedServiceFromRoute = serviceParam ? decodeURIComponent(serviceParam) : "";
  const isServiceDetailView = Boolean(serviceParam);

  const dispatch = useDispatch();
  const { clientId, id: loggedInUserId } = useSelector(getUserData);
  const fallbackProgram = useMemo(() => getPreventativeMaintenanceProgram(clientId), [clientId]);
  const [program, setProgram] = useState<PmProgramData>(() =>
    buildEmptyPmProgram(fallbackProgram.clientName, fallbackProgram.portfolioName),
  );
  const [isLoading, setIsLoading] = useState(true);
  //   const [loadError, setLoadError] = useState<string | null>(null);
  const dashboard: PmDashboardData = useMemo(() => getPmDashboardData(clientId, program), [clientId, program]);

  const [activeView, setActiveView] = useState<PmViewMode>("status");
  const [selectedServiceId, setSelectedServiceId] = useState(program.services[0]?.id ?? "");
  const [selectedSiteId, setSelectedSiteId] = useState(program.services[0]?.sites[0]?.id ?? "");
  const [selectedVisitId, setSelectedVisitId] = useState(program.services[0]?.sites[0]?.visits[0]?.id ?? "");

  useEffect(() => {
    dispatch(setPageNameHeader("Preventative Maintenance"));
  }, []);

  useEffect(() => {
    if (!program.services.length) {
      setSelectedServiceId("");
      return;
    }

    if (selectedServiceFromRoute) {
      const matchedService = program.services.find((service) => service.id === selectedServiceFromRoute);
      setSelectedServiceId(matchedService?.id ?? program.services[0]?.id ?? "");
      return;
    }

    setSelectedServiceId(program.services[0]?.id ?? "");
  }, [program, selectedServiceFromRoute]);

  useEffect(() => {
    let isMounted = true;

    const fetchPMData = async () => {
      setIsLoading(true);
      // setLoadError(null);
      setProgram(buildEmptyPmProgram(fallbackProgram.clientName, fallbackProgram.portfolioName));

      try {
        const response = await FirebaseDataService.getPreventitiveMaintenance(clientId, loggedInUserId);
        const liveProgram = normalizePreventativeMaintenanceProgram(response, clientId);

        if (!isMounted) {
          return;
        }

        setProgram(liveProgram);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        // setLoadError("Unable to load live preventitive maintenance data.");
        setProgram(fallbackProgram);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void fetchPMData();

    return () => {
      isMounted = false;
    };
  }, [clientId, fallbackProgram, loggedInUserId]);

  const selectedService = useMemo(
    () => program.services.find((service) => service.id === selectedServiceId) ?? program.services[0],
    [program, selectedServiceId],
  );

  useEffect(() => {
    setSelectedSiteId(selectedService?.sites[0]?.id ?? "");
  }, [selectedService]);

  const selectedSite = useMemo(
    () => selectedService?.sites.find((site) => site.id === selectedSiteId) ?? selectedService?.sites[0],
    [selectedService, selectedSiteId],
  );

  const siteVisits = useMemo(() => {
    return [...(selectedSite?.visits ?? [])].sort((left, right) => monthSortValue(right) - monthSortValue(left));
  }, [selectedSite]);

  useEffect(() => {
    const preferredVisit = siteVisits.find((visit) =>
      activeView === "status" ? visit.status === "completed" : visit.status !== "completed",
    );
    setSelectedVisitId(preferredVisit?.id ?? siteVisits[0]?.id ?? "");
  }, [siteVisits, activeView]);

  const selectedVisit = useMemo(
    () => siteVisits.find((visit) => visit.id === selectedVisitId) ?? siteVisits[0],
    [siteVisits, selectedVisitId],
  );

  const propertyCount = useMemo(() => {
    return new Set(program.services.flatMap((service) => service.sites.map((site) => site.id))).size;
  }, [program]);

  const pendingVisits = useMemo(() => {
    return program.services
      .flatMap((service) => service.sites)
      .flatMap((site) => site.visits)
      .reduce((count, visit) => (visit.status === "completed" ? count : count + visit.requiredCount), 0);
  }, [program]);

  const getServiceMonthlyCount = (service: PmService, month: string) => {
    return service.sites
      .flatMap((site) => site.visits)
      .filter((visit) => visit.month === month)
      .reduce((count, visit) => {
        if (activeView === "status") {
          return count + visit.completedCount;
        }

        return visit.status === "completed" ? count : count + visit.requiredCount;
      }, 0);
  };

  const getServiceTotals = (service: PmService) => {
    return service.sites
      .flatMap((site) => site.visits)
      .reduce(
        (totals, visit) => {
          totals.required += visit.requiredCount;
          totals.completed += visit.completedCount;
          totals.pending += visit.status === "completed" ? 0 : visit.requiredCount;
          return totals;
        },
        { required: 0, completed: 0, pending: 0 },
      );
  };

  const getSiteTotals = (site: PmSite) => {
    return site.visits.reduce(
      (totals, visit) => {
        totals.required += visit.requiredCount;
        totals.completed += visit.completedCount;
        totals.pending += visit.status === "completed" ? 0 : visit.requiredCount;
        return totals;
      },
      { required: 0, completed: 0, pending: 0 },
    );
  };

  const getStaticMonthlyCount = (month: string, row: PmProgramData["staticData"][number]) => {
    const key = month.toLowerCase() as keyof typeof row;
    const value = row[key];

    return typeof value === "number" ? value : 0;
  };

  const downloadVisitReport = (visit: PmVisitRecord) => {
    if (visit.status !== "completed") {
      return;
    }

    const fallbackName = `${selectedService?.serviceName ?? "PM-Report"}-${selectedSite?.siteName ?? "Site"}-${visit.month}-${visit.year}.txt`;
    const reportHref = buildPmReportDownload({
      clientName: program.clientName,
      serviceName: selectedService?.serviceName ?? "",
      siteName: selectedSite?.siteName ?? "",
      visit,
    });

    const anchor = document.createElement("a");
    anchor.href = reportHref;
    anchor.download = visit.reportFileName ?? fallbackName;
    anchor.click();
  };

  //   const dataStatusText = loadError ?? (isLoading ? "Loading live Maximo data..." : "Live Maximo data loaded");

  return (
    <Content>
      <PageShell>
        <PageStack>
          <HeroCard>
            <HeroMain>
              <HeroText>
                <PageTitleRow>
                  <PageTitle>Preventative Maintenance</PageTitle>
                  <HeaderMeta>
                    <HeaderMetaItem>{propertyCount} Properties</HeaderMetaItem>
                    <HeaderMetaDivider />
                    <HeaderMetaItem>{program.services.length} Services</HeaderMetaItem>
                    {/* <HeaderMetaDivider />
                    <HeaderMetaItem>{dataStatusText}</HeaderMetaItem> */}
                  </HeaderMeta>
                </PageTitleRow>
                <PageSub>
                  {program.clientName} — {program.portfolioName}
                </PageSub>
              </HeroText>
            </HeroMain>
          </HeroCard>

          <FilterTabs>
            <FilterTab $active={activeView === "status"} onClick={() => setActiveView("status")}>
              Status
            </FilterTab>
            <FilterTab $active={activeView === "scheduled"} onClick={() => setActiveView("scheduled")}>
              Scheduled
            </FilterTab>
          </FilterTabs>

          {isServiceDetailView && (
            <BackButton type="button" onClick={() => navigate("/preventative-maintenance")}>
              Back to Program Overview
            </BackButton>
          )}

          {isLoading ? (
            <LoadingPanel>
              <LoadingSpinner />
              <LoadingText>Loading preventative maintenance data...</LoadingText>
            </LoadingPanel>
          ) : (
            <>
              {!isServiceDetailView && (
                <>
                  <SummaryGrid>
                    <SummaryCard>
                      <SummaryContent>
                        <SummaryLabel>PM Services</SummaryLabel>
                        <SummaryValue>{program.services.length}</SummaryValue>
                        <SummarySub>Active fixed-frequency service programs</SummarySub>
                      </SummaryContent>
                    </SummaryCard>
                    <SummaryCard>
                      <SummaryContent>
                        <SummaryLabel>Completed ({dashboard.ytd.label})</SummaryLabel>
                        <SummaryValue $tone="green">{dashboard.ytd.completed.toLocaleString()}</SummaryValue>
                        <SummarySub>{dashboard.ytd.total.toLocaleString()} required visits in program</SummarySub>
                      </SummaryContent>
                    </SummaryCard>
                  </SummaryGrid>

                  <Panel>
                    <PanelHeader>
                      <div>
                        <PanelTitle>Program Overview</PanelTitle>
                        <PanelSub>
                          Review service compliance first, then drill into the matching site and service period below.
                        </PanelSub>
                      </div>
                      <PanelHeaderMeta>
                        {activeView === "status"
                          ? `${dashboard.ytd.completed.toLocaleString()} completed`
                          : `${pendingVisits.toLocaleString()} open`}
                      </PanelHeaderMeta>
                    </PanelHeader>
                    <TableScroll>
                      <OverviewTable>
                        <thead>
                          <tr>
                            <StickyHeaderCell>Service</StickyHeaderCell>
                            {PM_MONTHS.map((month) => (
                              <MonthHeaderCell key={month}>{month.toUpperCase()}</MonthHeaderCell>
                            ))}
                            <HeaderCell>Required</HeaderCell>
                            <HeaderCell>Completed</HeaderCell>
                            <HeaderCell>YTD</HeaderCell>
                          </tr>
                        </thead>
                        <tbody>
                          {program.services.map((service) => {
                            const totals = getServiceTotals(service);
                            const compliance = totals.required
                              ? Math.round((totals.completed / totals.required) * 100)
                              : 0;

                            return (
                              <OverviewRow
                                key={service.id}
                                $active={service.id === selectedService?.id}
                                $clickable
                                onClick={() => {
                                  setSelectedServiceId(service.id);
                                  navigate(`/pm/${encodeURIComponent(service.id)}`);
                                }}
                              >
                                <StickyBodyCell>
                                  <ServiceCellTitle>{service.serviceName}</ServiceCellTitle>
                                  <ServiceCellSub>{service.frequency}</ServiceCellSub>
                                </StickyBodyCell>
                                {PM_MONTHS.map((month) => {
                                  const count = getServiceMonthlyCount(service, month);
                                  return (
                                    <MonthCell key={`${service.id}-${month}`}>
                                      <MonthBadge $variant={activeView} $filled={count > 0}>
                                        {count > 0 ? count : ""}
                                      </MonthBadge>
                                    </MonthCell>
                                  );
                                })}
                                <NumericCell>{totals.required}</NumericCell>
                                <NumericCell $accent>{totals.completed}</NumericCell>
                                <BodyCell>
                                  <ProgressWrap>
                                    <ProgressTrack>
                                      <ProgressFill $pct={compliance} />
                                    </ProgressTrack>
                                    <ProgressLabel>{compliance}% YTD</ProgressLabel>
                                  </ProgressWrap>
                                </BodyCell>
                              </OverviewRow>
                            );
                          })}
                          {program.staticData.map((row) => (
                            <OverviewRow key={`static-${row.fixedScheduleReporting}-${row.frequency}`} $active={false}>
                              <StickyBodyCell>
                                <ServiceCellTitle>{row.fixedScheduleReporting}</ServiceCellTitle>
                                <ServiceCellSub>{`${row.frequency}`}</ServiceCellSub>
                              </StickyBodyCell>
                              {PM_MONTHS.map((month) => {
                                const count = getStaticMonthlyCount(month, row);
                                return (
                                  <MonthCell key={`${row.fixedScheduleReporting}-${month}`}>
                                    <MonthBadge $variant={activeView} $filled={count > 0}>
                                      {count > 0 ? count : ""}
                                    </MonthBadge>
                                  </MonthCell>
                                );
                              })}
                              <NumericCell>{"-"}</NumericCell>
                              <NumericCell>-</NumericCell>
                              <BodyCell>-</BodyCell>
                            </OverviewRow>
                          ))}
                        </tbody>
                      </OverviewTable>
                    </TableScroll>
                  </Panel>
                </>
              )}

              {isServiceDetailView && (
                <DetailGrid>
                  <Panel>
                    <PanelHeader>
                      <div>
                        <PanelTitle>Locations for {selectedService?.serviceName}</PanelTitle>
                        <PanelSub>{selectedService?.description}</PanelSub>
                      </div>
                    </PanelHeader>
                    <LeftTableScroll>
                      <DetailTable>
                        <thead>
                          <tr>
                            <LeftHeaderCell>Site</LeftHeaderCell>
                            <LeftHeaderCell>Frequency</LeftHeaderCell>
                            <LeftHeaderCell>Required</LeftHeaderCell>
                            <LeftHeaderCell>Completed</LeftHeaderCell>
                            <LeftHeaderCell>Open</LeftHeaderCell>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedService?.sites.map((site) => {
                            const totals = getSiteTotals(site);
                            return (
                              <DetailRow
                                key={site.id}
                                $active={site.id === selectedSite?.id}
                                onClick={() => setSelectedSiteId(site.id)}
                              >
                                <LeftBodyCell>
                                  <ServiceCellTitle>{site.siteName}</ServiceCellTitle>
                                  {/* <ServiceCellSub>
                                {site.city} · {site.propertyCode}
                              </ServiceCellSub> */}
                                </LeftBodyCell>
                                <LeftBodyCell>{site.frequency}</LeftBodyCell>
                                <LeftNumericCell>{totals.required}</LeftNumericCell>
                                <LeftNumericCell $accent>{totals.completed}</LeftNumericCell>
                                <LeftNumericCell>{totals.pending}</LeftNumericCell>
                              </DetailRow>
                            );
                          })}
                        </tbody>
                      </DetailTable>
                    </LeftTableScroll>
                  </Panel>

                  <Panel>
                    <PanelHeader>
                      <div>
                        <PanelTitle>{selectedSite?.siteName}</PanelTitle>
                        <PanelSub>Select a completed period to download the service report.</PanelSub>
                      </div>
                    </PanelHeader>
                    <RightTableScroll>
                      <DetailTable>
                        <thead>
                          <tr>
                            <RightHeaderCell>Year</RightHeaderCell>
                            <RightHeaderCell>Month</RightHeaderCell>
                            <RightHeaderCell>Status</RightHeaderCell>
                            <RightHeaderCell>Due</RightHeaderCell>
                            <RightHeaderCell>Completed</RightHeaderCell>
                          </tr>
                        </thead>
                        <tbody>
                          {siteVisits.map((visit) => (
                            <DetailRow
                              key={visit.id}
                              $active={visit.id === selectedVisit?.id}
                              onClick={() => {
                                setSelectedVisitId(visit.id);
                                downloadVisitReport(visit);
                              }}
                            >
                              <RightBodyCell>{visit.year}</RightBodyCell>
                              <RightBodyCell>{visit.month}</RightBodyCell>
                              <RightBodyCell>
                                <StatusPill $status={visit.status}>{statusLabelMap[visit.status]}</StatusPill>
                              </RightBodyCell>
                              <RightBodyCell>{formatPmDateTime(visit.dueDate)}</RightBodyCell>
                              <RightBodyCell>{formatPmDateTime(visit.completedDate)}</RightBodyCell>
                            </DetailRow>
                          ))}
                        </tbody>
                      </DetailTable>
                    </RightTableScroll>
                  </Panel>
                </DetailGrid>
              )}
            </>
          )}
        </PageStack>
      </PageShell>
    </Content>
  );
};

export default PreventativeMaintenance;

const PageStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-bottom: 36px;
`;

const PageShell = styled.div`
  width: 100%;
  background: ${colors.white.default};
  box-shadow: 0px 4px 120px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
`;

const HeroCard = styled.div`
  background: ${colors.white.default};
  border-bottom: 1px solid ${colors.grey.light11};
  padding: 0 0 14px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const HeroMain = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const HeroText = styled.div`
  min-width: 0;
`;

const PageTitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.2;
  color: ${colors.black.dark1};
`;

const HeaderMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${colors.grey.dark};
  font-size: 11px;
  font-weight: 500;
`;

const HeaderMetaItem = styled.span`
  white-space: nowrap;
`;

const HeaderMetaDivider = styled.span`
  width: 1px;
  height: 14px;
  background: ${colors.grey.light12};
`;

const PageSub = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: ${colors.grey.dark5};
`;

const FilterTabs = styled.div`
  display: inline-flex;
  padding: 4px;
  border-radius: 16px;
  background: ${colors.grey.light6};
  border: 1px solid ${colors.grey.light11};
  width: fit-content;
`;

const FilterTab = styled.button<{ $active: boolean }>`
  border: 0;
  background: transparent;
  color: ${colors.grey.dark};
  padding: 8px 18px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.02em;
  border-radius: 12px;

  ${({ $active }) =>
    $active &&
    css`
      background: ${colors.white.default};
      color: ${colors.black.dark1};
      box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.08);
    `}
`;

const BackButton = styled.button`
  width: fit-content;
  border: 1px solid ${colors.grey.light11};
  background: ${colors.white.default};
  color: ${colors.grey.dark};
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
`;

const LoadingPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 260px;
  border: 1px solid ${colors.grey.light11};
  border-top: 4px solid ${colors.kleenway.green};
  border-radius: 6px;
  background: ${colors.white.default};
`;

const LoadingSpinner = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid ${colors.grey.light11};
  border-top-color: ${colors.kleenway.green};
  animation: pmSpin 0.8s linear infinite;

  @keyframes pmSpin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  font-size: 12px;
  color: ${colors.grey.dark};
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  background: ${colors.white.default};
  border: 0.5px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  padding: 20px 22px;
  position: relative;
  overflow: hidden;
  border-top: 4px solid ${colors.kleenway.green};

  //   &::after {
  //     content: "";
  //     position: absolute;
  //     left: 0;
  //     right: 0;
  //     bottom: 0;
  //     height: 3px;
  //     background: ${colors.kleenway.green};
  //   }
`;

const SummaryContent = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${colors.grey.dark};
`;

const SummaryValue = styled.div<{ $tone?: "green" }>`
  margin-top: 8px;
  font-size: 26px;
  line-height: 1.1;
  color: ${({ $tone }) => ($tone === "green" ? "#1d9e75" : colors.black.dark1)};
  font-weight: 700;
`;

const SummarySub = styled.div`
  margin-top: 6px;
  font-size: 11px;
  color: ${colors.grey.dark5};
`;

const Panel = styled.div`
  background: ${colors.white.default};
  border: 1px solid ${colors.grey.light11};
  border-top: 4px solid ${colors.kleenway.green};
  border-radius: 6px;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: 16px 16px 0;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const PanelTitle = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${colors.grey.dark};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const PanelSub = styled.div`
  margin-top: 4px;
  font-size: 10px;
  color: ${colors.grey.dark5};
`;

const PanelHeaderMeta = styled.div`
  color: ${colors.grey.dark};
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
`;

const TableScroll = styled.div`
  overflow-x: auto;
  padding: 16px;
`;

const OverviewTable = styled.table`
  width: 100%;
  min-width: 1180px;
  border-collapse: separate;
  border-spacing: 0;
`;

const DetailTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 14px;
  padding: 0 16px 16px;
`;

const RightTableScroll = styled.div`
  overflow-x: auto;
`;

const LeftTableScroll = styled.div`
  overflow-x: auto;
`;

const HeaderCell = styled.th`
  padding: 12px 14px;
  background: ${colors.grey.light6};
  color: ${colors.grey.dark5};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-align: left;
  border-bottom: 1px solid ${colors.grey.light11};
`;

const MonthHeaderCell = styled(HeaderCell)`
  text-align: center;
`;

const StickyHeaderCell = styled(HeaderCell)`
  position: sticky;
  left: 0;
  z-index: 2;
`;

const OverviewRow = styled.tr<{ $active: boolean; $clickable?: boolean }>`
  cursor: ${({ $clickable = false }) => ($clickable ? "pointer" : "default")};

  td {
    background: ${({ $active }) => ($active ? colors.grey.light6 : colors.white.default)};
  }
`;

const DetailRow = styled.tr<{ $active: boolean }>`
  cursor: pointer;

  td {
    background: ${({ $active }) => ($active ? colors.grey.light6 : colors.white.default)};
  }
`;

const BodyCell = styled.td`
  padding: 14px 12px;
  color: ${colors.grey.dark};
  font-size: 12px;
  vertical-align: middle;
  border: 1px solid ${colors.grey.light11};
`;

const RightHeaderCell = styled(HeaderCell)`
  padding: 9px 8px;
  font-size: 9px;
  white-space: nowrap;
  width: 1%;
`;

const RightBodyCell = styled(BodyCell)`
  padding: 9px 8px;
  font-size: 11px;
  white-space: nowrap;
`;

const LeftHeaderCell = styled(HeaderCell)`
  padding: 9px 8px;
  font-size: 9px;
  white-space: nowrap;
`;

const LeftBodyCell = styled(BodyCell)`
  padding: 9px 8px;
  font-size: 11px;
`;

const LeftNumericCell = styled(BodyCell)<{ $accent?: boolean }>`
  padding: 9px 8px;
  font-size: 11px;
  white-space: nowrap;
  color: ${({ $accent }) => ($accent ? "#1d9e75" : colors.black.dark1)};
  font-weight: 600;
`;

const StickyBodyCell = styled(BodyCell)`
  position: sticky;
  left: 0;
  z-index: 1;
  min-width: 220px;
`;

const MonthCell = styled(BodyCell)`
  text-align: center;
`;

const NumericCell = styled(BodyCell)<{ $accent?: boolean }>`
  color: ${({ $accent }) => ($accent ? "#1d9e75" : colors.black.dark1)};
  font-weight: 600;
  font-size: 13px;
`;

const ServiceCellTitle = styled.div`
  color: #2563eb;
  font-size: 13px;
  font-weight: 500;
`;

const ServiceCellSub = styled.div`
  margin-top: 3px;
  color: ${colors.grey.dark5};
  font-size: 11px;
`;

const MonthBadge = styled.div<{ $variant: PmViewMode; $filled: boolean }>`
  min-height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $filled }) => ($filled ? colors.white.default : colors.grey.light17)};
  background: ${({ $variant, $filled }) => {
    if (!$filled) {
      return "#f8fafc";
    }

    return $variant === "status" ? "#1d9e75" : "#c18a1a";
  }};
`;

const ProgressWrap = styled.div`
  min-width: 96px;
`;

const ProgressTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  background: ${colors.grey.light3};
`;

const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  border-radius: 999px;
  background: #1d9e75;
`;

const ProgressLabel = styled.div`
  margin-top: 6px;
  color: ${colors.grey.dark5};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 14px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const StatusPill = styled.span<{ $status: PmVisitRecord["status"] }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 88px;
  padding: 7px 12px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $status }) => ($status === "completed" ? "#0f6e56" : $status === "scheduled" ? "#9a6700" : "#8f3b2f")};
  background: ${({ $status }) =>
    $status === "completed" ? "#dff6ee" : $status === "scheduled" ? "#fff4d6" : "#fde8e5"};
`;
